import React from 'react'
import { ContainerRow, VerticalLine } from './styles'
import { AlertMessage, RowContentPart, RowInfoPart, TimelineConducts, TimelineOmics } from '~/components/shared'
import { IAdmissionRowSlots, IHealthProcedures } from '~/interfaces'
import { message } from '~/services'
import { mapperTimelineViewByMonthsFrom } from '~/utils/dashboard/timeline/general'
import { byTypeEqualToAdmission, byTypeExceptAdmission, filterHealthProceduresPer, timelineFilters } from '~/utils'
import { useMaternalInfantHealthProcedures } from '~/context'
import { createAdmissionsSlotsFrom } from '~/utils/dashboard/timeline/mappers'

interface TimelineViewByMonthsProps {
  healthProcedures: IHealthProcedures[]
  onSelectEvent: (healthProcedures: IHealthProcedures[]) => void
}

export const TimelineViewByMonths = ({ healthProcedures, onSelectEvent }: TimelineViewByMonthsProps): JSX.Element => {
  const { typeFiltersActive, dateFiltersActive, filterTags } = useMaternalInfantHealthProcedures()

  const applyTimelineFilters = (healthProcedures: IHealthProcedures[]): IHealthProcedures[] => {
    const searchTerms = filterTags?.filter(tag => tag.type === 'searchTerm').map(tag => tag.text)
    return timelineFilters(healthProcedures)
      .perDate(dateFiltersActive)
      .perType(typeFiltersActive)
      .perMultipleTextTerms(searchTerms).value
  }

  const filteredEvents = applyTimelineFilters(healthProcedures)

  if (!filteredEvents || filteredEvents.length <= 0) {
    return <AlertMessage>{message('maternalInfant.empty_timeline_section')}</AlertMessage>
  }

  const structuredData = mapperTimelineViewByMonthsFrom(filteredEvents)

  const handleHealthProcedures = (selectedHealthProcedures: IHealthProcedures[]) => {
    onSelectEvent(selectedHealthProcedures)
  }

  const handleAdmissions = (selectedAdmissions: IAdmissionRowSlots[]) => {
    const rawAdmission = healthProcedures.find(healthProcedure => healthProcedure.id === selectedAdmissions[0].id)

    if (rawAdmission) {
      const admissionSlots = createAdmissionsSlotsFrom([rawAdmission], healthProcedures)
      onSelectEvent(admissionSlots[0].healthProcedures)
    }
  }

  return (
    <>
      {structuredData.years.map((year, yearIndex, yearArray) => {
        return (
          <React.Fragment key={`year-${year.id}`}>
            <ContainerRow>
              <RowInfoPart isFilled={true} period={`${year.id} (${year.yearsRowSlotsTotal})`}>
                <VerticalLine isFirst={yearIndex === 0} isLast={false} />
              </RowInfoPart>
              <RowContentPart isFull={true} isFilled={false}>
                {/* HEADER ROW IS EMPTY */}
              </RowContentPart>
            </ContainerRow>

            {year.months.map((month, monthIndex, monthArray) => {
              const { rowSlots } = month
              const healthProceduresWithoutAdmissions = filterHealthProceduresPer(rowSlots, byTypeExceptAdmission)
              const admissions = filterHealthProceduresPer(rowSlots as IAdmissionRowSlots[], byTypeEqualToAdmission)

              return (
                <ContainerRow key={`month-${month.id}`}>
                  <RowInfoPart isFilled={false} period={`${month.id.substring(0, 3)}`}>
                    <VerticalLine
                      isFirst={false}
                      isLast={monthIndex === monthArray.length - 1 && yearIndex === yearArray.length - 1}
                    />
                  </RowInfoPart>
                  <RowContentPart isFull={false} isFilled={false}>
                    {/* CONTENT ROW */}
                    {healthProceduresWithoutAdmissions?.length > 0 && (
                      <TimelineOmics
                        period="months"
                        healthProcedures={healthProceduresWithoutAdmissions}
                        handleClick={handleHealthProcedures}
                      />
                    )}
                    {admissions?.length > 0 && (
                      <TimelineConducts healthProcedures={admissions} period="months" handleClick={handleAdmissions} />
                    )}
                  </RowContentPart>
                </ContainerRow>
              )
            })}
          </React.Fragment>
        )
      })}
    </>
  )
}
